/** Text utilities for cleaning Yang Bojun's source data and measuring similarity. */

const CIRCLED_NOTE_MARKS = /[①-⑳⑴-⒇㉑-㉟㊱-㊿]/g;
const CJK = /[一-鿿]/;

export function stripNoteMarks(value: string): string {
  return value.replace(CIRCLED_NOTE_MARKS, "").replace(/\s+/g, " ").trim();
}

export function cleanTranslation(value: string): string {
  return value
    .replace(/^译文：|^譯文：/, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function stripAnnotationHeader(value: string): string {
  return value.replace(/^【?\s*注[释釋]\s*】?\s*[:：]?/, "").trim();
}

export function truncate(value: string, max: number): string {
  const normalized = value.replace(/\s+/g, " ").trim();
  return normalized.length > max ? `${normalized.slice(0, max - 1)}…` : normalized;
}

/** First clause of a sentence — used to make options uniform in shape/length. */
export function firstClause(value: string, max = 60): string {
  const normalized = cleanTranslation(value).replace(/^[“"「]/, "");
  const cut = normalized.split(/[。！？；;]/)[0] ?? normalized;
  return truncate(cut, max);
}

/** Pull short glossary terms (the head word before "——") from annotation lines. */
export function extractAnnotationTerms(annotations: string): string[] {
  if (!annotations) return [];
  const terms = annotations
    .split(/\n+/)
    .map((line) => stripAnnotationHeader(line.replace(/^注[释釋]：/, "")))
    .map((line) => line.replace(CIRCLED_NOTE_MARKS, "").trim())
    .map((line) => (line.split(/——|--|－|—/)[0] ?? "").trim())
    .filter((term) => term && term.length <= 6 && !/[。！？，、：；,.]/.test(term) && CJK.test(term));
  return Array.from(new Set(terms)).slice(0, 8);
}

/** Annotation lines that document a disputed / superseded reading. */
export function disputedGlossLines(annotations: string): string[] {
  if (!annotations) return [];
  return annotations
    .split(/\n+/)
    .map((line) => stripNoteMarks(stripAnnotationHeader(line)))
    .filter((line) =>
      /(也有人|有人說|有人说|旧[注說说]|舊[注說说]|后[人世]|後[人世]|误解|誤解|不作|不是|当作|當作|本义|本義|后代|後代|朱熹|集注)/.test(
        line,
      ),
    )
    .map((line) => line.trim())
    .filter((line) => line.length >= 8);
}

export function cjkSet(value: string): Set<string> {
  const set = new Set<string>();
  for (const ch of value) if (CJK.test(ch)) set.add(ch);
  return set;
}

export function jaccard(a: Set<string>, b: Set<string>): number {
  if (!a.size || !b.size) return 0;
  let inter = 0;
  for (const ch of a) if (b.has(ch)) inter += 1;
  return inter / (a.size + b.size - inter);
}
